import { defineStore } from 'pinia'
import { client } from '@/services/client'
import { useSpace } from '@/store/modules/App/space'
import type {
  Component,
  ComponentGroup,
  Story,
  ComponentVersion,
  Preset,
} from '@/types'
import { filterByPermissions } from '@/utils/filterByPermissions'

export type State = {
  components: Component[]
  component_groups: ComponentGroup[]
  grouped_components: Component[]
  loadComponentPromise: Promise<void> | null
}

const state = (): State => ({
  components: [],
  component_groups: [],
  grouped_components: [],
  loadComponentPromise: null,
})

export const useComponents = defineStore('component', {
  state,
  getters: {
    getAllowedComponents: (state: State): Component[] => {
      return filterByPermissions(
        state.components,
        'managed_component_ids',
        'blocked_manage_component_ids',
        'managed_component_group_uuids',
      )
    },
    getAllowedComponentGroups: (state: State): ComponentGroup[] => {
      return filterByPermissions(
        state.component_groups,
        'managed_component_group_uuids',
        'blocked_manage_component_group_uuids',
        'managed_component_ids',
      )
    },
    getAllowedGroupedComponents: (state: State): Component[] => {
      return filterByPermissions(
        state.grouped_components,
        'managed_component_ids',
        'blocked_manage_component_ids',
      )
    },
    getComponents: (state: State): Component[] => state.components || [],
    getGroupedComponents: (state: State): Component[] =>
      state.grouped_components || [],
    getComponentGroups: (state: State): ComponentGroup[] =>
      state.component_groups || [],
    getStoryComponents:
      (state: State) =>
      (story: Story | Partial<Story>): Component[] => {
        const storyString = JSON.stringify(story)
        return state.components.filter((c) => {
          return storyString.includes(c.name)
        })
      },
  },
  actions: {
    getComponent(name: string): Component | undefined {
      return this.components.find((component) => component.name === name)
    },

    setComponentsAndGroups({ components, component_groups }): void {
      this.components = components
      this.component_groups = component_groups
    },

    setGroupComponents(data): void {
      this.grouped_components = data.components
    },

    updateComponents(data): void {
      this.components = [...this.components, data]
    },

    updateComponent(data): void {
      const index = this.components.findIndex(
        (component) => component.id === data.id,
      )
      if (index !== -1) {
        this.components[index] = data
      }
    },

    setComponentPromise(promise): void {
      this.loadComponentPromise = promise
    },

    removeComponent(id): void {
      this.components = this.components.filter(
        (component) => component.id !== id,
      )
    },

    updateGroupNameInList({ newGroupName, groupId }): void {
      const updatedGroup = this.component_groups.find(
        (folder) => folder.id === groupId,
      )
      if (updatedGroup) {
        updatedGroup.name = newGroupName
      }
    },

    async loadComponents(payload: {
      sortBy?: string
      groupUuid?: string
      spaceId: number
      isRoot?: boolean
    }): Promise<void> {
      try {
        if (this.loadComponentPromise) {
          return this.loadComponentPromise
        }

        const spaceStore = useSpace()

        const { sortBy, groupUuid, spaceId, isRoot = false } = payload
        const activeSpaceId = spaceId ?? spaceStore.space.id

        const params = []
        if (sortBy) {
          params.push(`sort_by=${sortBy}`)
        }
        if (groupUuid && !isRoot) {
          params.push(`in_group=${groupUuid}`)
        }
        if (isRoot) {
          params.push('is_root=true')
        }

        const queryString = params.length ? `?${params.join('&')}` : ''
        const promise = client.get(
          `spaces/${activeSpaceId}/components${queryString}`,
          {
            region: spaceStore.getSpaceRegion,
          },
        )

        this.setComponentPromise(promise)

        const { data } = await promise

        this.setComponentPromise(null)

        if (groupUuid && !isRoot) {
          return this.setGroupComponents(data)
        }

        this.setComponentsAndGroups({
          components: data.components,
          component_groups: data.component_groups,
        })

        delete data.components
        delete data.component_groups
      } catch (err) {
        this.setComponentPromise(null)
        return Promise.reject(err)
      }
    },

    async deleteGroup(payload): Promise<void> {
      const { spaceId, groupId } = payload
      return client.delete(`spaces/${spaceId}/component_groups/${groupId}`, {
        region: useSpace().region,
      })
    },

    async createComponentGroup(payload): Promise<void> {
      const { spaceId, groupName, parentId = null } = payload
      await client.post(
        `spaces/${spaceId}/component_groups`,
        {
          component_group: {
            name: groupName,
            parent_id: parentId,
          },
        },
        {
          region: useSpace().region,
        },
      )
      return this.loadComponents({ spaceId })
    },

    async updateComponentGroup({
      payload,
      spaceId,
      groupId,
    }: {
      payload: { name?: string; parent_id?: number }
      spaceId: string
      groupId?: number
    }): Promise<void> {
      await client.put(
        `spaces/${spaceId}/component_groups/${groupId}`,
        {
          component_group: { ...payload },
        },
        {
          region: useSpace().region,
        },
      )

      this.updateGroupNameInList({ newGroupName: payload.name, groupId })

      return this.loadComponents({ spaceId })
    },

    async createComponents(payload: {
      component: Component
      spaceId: string | number
    }): Promise<Component> {
      const { component, spaceId } = payload

      try {
        const { data } = await client.post(
          `spaces/${spaceId}/components`,
          component,
          {
            region: useSpace().region,
          },
        )

        this.updateComponents(data.component)
        return Promise.resolve(data.component)
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async saveComponent(payload: {
      spaceId: number
      componentId: number
      data: Partial<Component>
    }): Promise<void> {
      const { spaceId, componentId, data } = payload

      try {
        await client.put(
          `spaces/${spaceId}/components/${componentId}`,
          {
            component: { ...data },
            update_content: true,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async moveComponents(payload): Promise<void> {
      const { spaceId, groupId, ids = [] } = payload

      try {
        await client.post(
          `spaces/${spaceId}/components/bulk_update`,
          {
            component: {
              component_group_id: groupId,
            },
            ids,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async renameAttribute(payload: {
      spaceId: string | number
      componentId: string | number
      newName: string
      oldName: string
      storyId?: string | number
    }): Promise<void> {
      const { spaceId, storyId, componentId, newName, oldName } = payload

      await client.post(
        `spaces/${spaceId}/components/${componentId}/rename_attribute`,
        {
          new_name: newName,
          old_name: oldName,
          story_id: storyId,
        },
        {
          region: useSpace().region,
        },
      )

      return this.loadComponents({ spaceId })
    },

    async createPreset(payload): Promise<void> {
      const { spaceId, preset } = payload

      try {
        await client.post(
          `spaces/${spaceId}/presets`,
          {
            preset,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadPreset(payload): Promise<Preset> {
      const { spaceId, presetId } = payload

      try {
        const { data } = await client.get(
          `spaces/${spaceId}/presets/${presetId}`,
          {
            region: useSpace().region,
          },
        )

        return data.preset.preset
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async editPreset(payload): Promise<void> {
      const { spaceId, preset, presetId } = payload

      try {
        await client.put(
          `spaces/${spaceId}/presets/${presetId}`,
          {
            preset,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async deletePreset(payload): Promise<void> {
      const { spaceId, presetId } = payload

      if (!presetId) {
        return Promise.resolve()
      }

      try {
        await client.delete(`spaces/${spaceId}/presets/${presetId}`, {
          region: useSpace().region,
        })
        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadPresets(payload): Promise<Preset[]> {
      const { spaceId, componentId } = payload

      const { data } = await client.get(`spaces/${spaceId}/presets`, {
        params: {
          component_id: componentId,
        },
        region: useSpace().region,
      })

      return data.presets
    },

    async deleteComponent(payload): Promise<void> {
      const { componentId, spaceId } = payload
      await client.delete(`spaces/${spaceId}/components/${componentId}`, {
        region: useSpace().region,
      })
      return this.removeComponent(componentId)
    },

    async loadComponentVersions(payload): Promise<ComponentVersion[]> {
      const { spaceId, apiParameters } = payload

      const response = await client.get(`spaces/${spaceId}/versions`, {
        params: apiParameters,
        region: useSpace().region,
      })

      return response
    },

    async previewComponentVersion(payload): Promise<Component> {
      const { spaceId, componentId, version } = payload

      const { data } = await client.get(
        `spaces/${spaceId}/components/${componentId}/component_versions/${version.id}`,
        {
          region: useSpace().region,
        },
      )
      return data.component_version
    },

    async restoreComponentVersion(payload): Promise<void> {
      const { spaceId, componentId, versionId } = payload

      try {
        await client.put(
          `spaces/${spaceId}/versions/${versionId}`,
          {
            model: 'components',
            model_id: componentId,
          },
          {
            region: useSpace().region,
          },
        )
        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadLatestFieldVersion(payload): Promise<ComponentVersion[]> {
      const { spaceId, componentId, keys } = payload

      const { data } = await client.get(
        `spaces/${spaceId}/field_versions/${componentId}`,
        {
          params: {
            fields: keys,
          },
          region: useSpace().region,
        },
      )

      return data.field_version
    },
  },
})
